import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import type {
  CmsResponse,
  PopularSearch,
  TopUseCase,
} from '@/features/chat/types/cms';
import { parseCmsFunnel } from '@/features/w2w/funnel/config/parse';
import type { CmsFunnel } from '@/features/w2w/funnel/config/schema';

interface CmsQueryParams {
  locale?: string;
  status?: string;
}

interface CmsFunnelQueryParams extends CmsQueryParams {
  funnelKey: string;
}

const FUNNEL_REQUEST_TIMEOUT_MS = 6000;

const FUNNEL_POPULATE = [
  'steps.question.answers',
  'steps.question.answers.icon',
  'steps.question.pronounTitles',
];

const cmsStatus =
  import.meta.env.VITE_APP_ENV === 'production' ? 'published' : 'draft';

const cmsApi = createApi({
  reducerPath: 'cmsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_API_BASE_URL as string}/v1/cms`,
  }),
  tagTypes: ['TopUseCases', 'PopularSearches'],
  endpoints: (builder) => ({
    getTopUseCases: builder.query<TopUseCase[], CmsQueryParams>({
      query: (params) => {
        const searchParams = new URLSearchParams({
          status: params?.status ?? cmsStatus,
        });
        if (params?.locale != null && params.locale.trim() !== '') {
          searchParams.set('locale', params.locale);
        }
        return `/top-usecases?${searchParams.toString()}`;
      },
      transformResponse: (response: CmsResponse<TopUseCase[]>): TopUseCase[] =>
        [...response.data].sort(
          (a: TopUseCase, b: TopUseCase) => a.displayOrder - b.displayOrder,
        ),
    }),
    getFunnel: builder.query<CmsFunnel | null, CmsFunnelQueryParams>({
      query: (params) => {
        const searchParams = new URLSearchParams({
          'filters[funnelKey][$eq]': params.funnelKey,
          status: params?.status ?? cmsStatus,
        });
        for (const populate of FUNNEL_POPULATE) {
          searchParams.append('populate', populate);
        }
        if (params?.locale != null && params.locale.trim() !== '') {
          searchParams.set('locale', params.locale);
        }
        return {
          url: `/funnels?${searchParams.toString()}`,
          timeout: FUNNEL_REQUEST_TIMEOUT_MS,
        };
      },
      transformResponse: (response: CmsResponse<unknown[]>): CmsFunnel | null =>
        parseCmsFunnel(response.data?.[0]),
    }),
    getPopularSearches: builder.query<PopularSearch[], CmsQueryParams>({
      query: (params) => {
        const searchParams = new URLSearchParams({
          status: params?.status ?? cmsStatus,
        });
        if (params?.locale != null && params.locale.trim() !== '') {
          searchParams.set('locale', params.locale);
        }
        return `/popular-searches?${searchParams.toString()}`;
      },
      transformResponse: (
        response: CmsResponse<PopularSearch[]>,
      ): PopularSearch[] =>
        [...response.data].sort(
          (a: PopularSearch, b: PopularSearch) =>
            a.displayOrder - b.displayOrder,
        ),
    }),
  }),
});

export { cmsApi };
export const {
  useGetTopUseCasesQuery,
  useGetPopularSearchesQuery,
  useGetFunnelQuery,
} = cmsApi;
